/**
 * Projects Page - Blog-style projects/shipments listing
 * 
 * SITEMAP NOTE: The following routes should be included in the sitemap:
 * - /projects (listing page)
 * - /projects/:id (individual post pages - dynamically generated)
 * 
 * These pages target long-tail SEO keywords like:
 * - "used Hanwha solar panels export"
 * - "solar panel shipment Pakistan"
 * - "decommissioned solar panels Dubai"
 */

import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Calendar, X, Sun } from "lucide-react";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "../components/DirectionalIcon";
import { buildLocalizedPath } from "../constants/languages";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Post descriptions are otherwise rendered as plain text (no markdown/HTML
// parsing) -- this is the one exception, recognizing a single lightweight
// pattern, [anchor text](/path), so a description can carry a real,
// crawlable internal link instead of an inert plain-text mention. Anything
// not matching this exact pattern renders unchanged, exactly as before.
const DESCRIPTION_LINK_PATTERN = /\[([^\]]+)\]\((\/[a-zA-Z0-9\-/]*)\)/g;

function renderDescriptionWithLinks(text, lang) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  DESCRIPTION_LINK_PATTERN.lastIndex = 0;
  while ((match = DESCRIPTION_LINK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <Link key={key++} to={buildLocalizedPath(match[2], lang)} className="text-gold hover:underline">
        {match[1]}
      </Link>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

// SEO Helper: Set meta description
const setMetaDescription = (description) => {
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = description;
};

// SEO Helper: Inject JSON-LD structured data
const injectJsonLd = (data) => {
  // Remove any existing JSON-LD script
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) existing.remove();
  
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

// SEO Helper: Remove JSON-LD on unmount
const removeJsonLd = () => {
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) existing.remove();
};

// Skeleton loader for cards
const SkeletonCard = () => (
  <div className="bg-slate-800/50 rounded-xl overflow-hidden animate-pulse">
    <div className="h-48 bg-slate-700"></div>
    <div className="p-6 space-y-3">
      <div className="h-4 bg-slate-700 rounded w-1/3"></div>
      <div className="h-6 bg-slate-700 rounded w-3/4"></div>
      <div className="h-4 bg-slate-700 rounded w-full"></div>
      <div className="h-4 bg-slate-700 rounded w-2/3"></div>
    </div>
  </div>
);

// Image Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev, postTitle }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNext, onPrev]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 end-4 p-2 text-white/80 hover:text-white transition-colors"
      >
        <X size={32} />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute start-4 p-3 text-white/80 hover:text-white bg-black/30 rounded-full transition-colors"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute end-4 p-3 text-white/80 hover:text-white bg-black/30 rounded-full transition-colors"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}
      
      <img
        src={images[currentIndex]}
        alt={`${postTitle} - M A Galaxy International Solar Panel Shipment`}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        loading="lazy"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-4 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

// Format date nicely
const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

// Post Card Component
const PostCard = ({ post }) => {
  const { t, i18n } = useTranslation();
  const coverImage = post.images && post.images.length > 0 ? post.images[0] : null;
  const displayTitle = post.title_translations?.[i18n.language] || post.title;
  const displayDescription = post.description_translations?.[i18n.language] || post.description;
  const excerpt = displayDescription.length > 120
    ? displayDescription.substring(0, 120) + "..."
    : displayDescription;

  return (
    <Link to={buildLocalizedPath(`/projects/${post.id}`, i18n.language)} data-testid={`project-card-${post.id}`}>
      <motion.div
        whileHover={{ y: -5 }}
        className="bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-gold/30 transition-all duration-300 h-full flex flex-col"
      >
        {/* Cover Image */}
        <div className="h-48 bg-navy-800 relative overflow-hidden">
          {coverImage ? (
            <img
              src={coverImage}
              alt={`${displayTitle} - M A Galaxy International Solar Panel Shipment`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-navy">
              <Sun size={48} className="text-gold/30" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
            <Calendar size={14} />
            {/* dir="ltr" keeps day/month/year in reading order under dir="rtl" ancestors (bidi reorders mixed digit/Latin runs) */}
            <span dir="ltr">{formatDate(post.date)}</span>
          </div>

          <h3 className="text-lg font-bold text-white mb-3 line-clamp-2">
            {displayTitle}
          </h3>
          
          <p className="text-slate-400 text-sm flex-1 mb-4">
            {excerpt}
          </p>
          
          <span className="text-gold text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">
            {t("projects.postCard.readMore")} <ArrowRight size={14} />
          </span>
        </div>
      </motion.div>
    </Link>
  );
};

// Single Post Page Component
export const ProjectPostPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${id}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setPost(data);
      } catch (err) {
        console.error("Failed to fetch post:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  const displayTitle = post ? (post.title_translations?.[i18n.language] || post.title) : "";
  const displayDescription = post ? (post.description_translations?.[i18n.language] || post.description) : "";

  // SEO: Set page title, meta description, and JSON-LD when post loads
  useEffect(() => {
    if (post) {
      // Set page title
      document.title = `${displayTitle} | M A Galaxy International`;

      // Set meta description (first 155 characters)
      const metaDesc = displayDescription.substring(0, 155);
      setMetaDescription(metaDesc);

      // Inject JSON-LD structured data
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": displayTitle,
        "datePublished": post.date,
        "dateModified": post.date,
        "author": {
          "@type": "Organization",
          "name": "M A Galaxy International"
        },
        "publisher": {
          "@type": "Organization",
          "name": "M A Galaxy International",
          "url": "https://magalaxyintl.com"
        },
        "description": metaDesc,
        "image": post.images && post.images.length > 0 ? post.images[0] : undefined
      };
      injectJsonLd(jsonLd);
    }

    // Cleanup on unmount
    return () => {
      removeJsonLd();
    };
  }, [post, displayTitle, displayDescription]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % post.images.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + post.images.length) % post.images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy pt-32 pb-20">
        <div className="container-custom">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-700 rounded w-32"></div>
            <div className="h-6 bg-slate-700 rounded w-48"></div>
            <div className="h-12 bg-slate-700 rounded w-3/4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-slate-700 rounded w-full"></div>
              <div className="h-4 bg-slate-700 rounded w-full"></div>
              <div className="h-4 bg-slate-700 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-navy pt-32 pb-20">
        <div className="container-custom text-center">
          <h1 className="text-3xl font-bold text-white mb-4">{t("projects.post.notFoundTitle")}</h1>
          <Link to={buildLocalizedPath("/projects", i18n.language)} className="inline-flex items-center gap-1 text-gold hover:underline">
            <ArrowLeft size={16} />
            {t("projects.post.notFoundBackLink")}
          </Link>
        </div>
      </div>
    );
  }

  // SEO-optimized alt text for images
  const imageAltText = `${displayTitle} - M A Galaxy International Solar Panel Shipment`;

  return (
    <div className="min-h-screen bg-navy pt-32 pb-20">
      <div className="container-custom max-w-4xl">
        {/* Back Button */}
        <Link
          to={buildLocalizedPath("/projects", i18n.language)}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-gold transition-colors mb-8"
          data-testid="back-to-projects"
        >
          <ArrowLeft size={18} />
          {t("projects.post.backLink")}
        </Link>

        {/* Date */}
        <div className="flex items-center gap-2 text-gold mb-4">
          <Calendar size={16} />
          {/* dir="ltr" keeps day/month/year in reading order under dir="rtl" ancestors (bidi reorders mixed digit/Latin runs) */}
          <span dir="ltr">{formatDate(post.date)}</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
          {displayTitle}
        </h1>

        {/* Description */}
        <div className="prose prose-lg prose-invert max-w-none mb-12">
          <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
            {renderDescriptionWithLinks(displayDescription, i18n.language)}
          </p>
        </div>

        {/* Photo Gallery */}
        {post.images && post.images.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold text-white mb-6">{t("projects.post.photosTitle")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {post.images.map((img, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className="cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={img}
                    alt={imageAltText}
                    className="w-full h-48 object-cover rounded-lg border border-slate-700 hover:border-gold/50 transition-colors"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-slate-800/50 rounded-xl p-8 text-center border border-slate-700 mb-12">
          <h3 className="text-2xl font-bold text-white mb-4">
            {t("projects.post.ctaTitle")}
          </h3>
          <p className="text-slate-400 mb-6">
            {t("projects.post.ctaSubtitle")}
          </p>
          <Link
            to={buildLocalizedPath("/contact", i18n.language)}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-colors"
            data-testid="post-contact-cta"
          >
            {t("projects.post.ctaButton")}
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* SEO Internal Links - Related Pages */}
        <div className="border-t border-slate-700 pt-12">
          <h3 className="text-xl font-bold text-white mb-6">{t("projects.post.relatedTitle")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to={buildLocalizedPath("/sell-panels", i18n.language)}
              className="bg-slate-800/30 rounded-lg p-6 border border-slate-700 hover:border-gold/50 transition-colors group"
            >
              <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-gold transition-colors">
                {t("projects.post.related1.title")}
              </h4>
              <p className="text-slate-400 text-sm">
                {t("projects.post.related1.description")}
              </p>
            </Link>

            <Link
              to={buildLocalizedPath("/buy-panels", i18n.language)}
              className="bg-slate-800/30 rounded-lg p-6 border border-slate-700 hover:border-gold/50 transition-colors group"
            >
              <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-gold transition-colors">
                {t("projects.post.related2.title")}
              </h4>
              <p className="text-slate-400 text-sm">
                {t("projects.post.related2.description")}
              </p>
            </Link>

            <Link
              to={buildLocalizedPath("/about", i18n.language)}
              className="bg-slate-800/30 rounded-lg p-6 border border-slate-700 hover:border-gold/50 transition-colors group"
            >
              <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-gold transition-colors">
                {t("projects.post.related3.title")}
              </h4>
              <p className="text-slate-400 text-sm">
                {t("projects.post.related3.description", { years: new Date().getFullYear() - 2000 })}
              </p>
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={post.images}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
            postTitle={displayTitle}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Projects Page Component
const ProjectsPage = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(12);

  // SEO: Set page title and meta description for listing page
  useEffect(() => {
    document.title = t("projects.meta.title");
    setMetaDescription(t("projects.meta.description"));

    // Cleanup: restore default title on unmount if needed
    return () => {
      // Meta description will be overwritten by next page
    };
  }, [t]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects`);
        const data = await res.json();
        setPosts(data);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const displayedPosts = posts.slice(0, displayCount);
  const hasMore = displayCount < posts.length;

  const loadMore = () => {
    setDisplayCount((prev) => prev + 12);
  };

  return (
    <div className="min-h-screen bg-navy">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-navy via-navy to-slate-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t("projects.hero.title")} <span className="text-gold">{t("projects.hero.titleAccent")}</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
              {t("projects.hero.subtitle", { years: new Date().getFullYear() - 2000 })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-slate-900">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <Sun size={64} className="mx-auto text-slate-600 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">{t("projects.emptyState.title")}</h3>
              <p className="text-slate-400">{t("projects.emptyState.description")}</p>
            </div>
          ) : (
            <>
              {/* Post Count */}
              <p className="text-slate-400 mb-8">
                {t("projects.postCount", { shown: displayedPosts.length, total: posts.length })}
              </p>

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayedPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <PostCard post={post} />
                  </motion.div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    className="px-8 py-4 bg-slate-800 text-white font-medium rounded-lg border border-slate-700 hover:border-gold hover:text-gold transition-all"
                    data-testid="load-more-projects"
                  >
                    {t("projects.loadMoreButton")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-navy">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t("projects.bottomCta.title")}
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            {t("projects.bottomCta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={buildLocalizedPath("/sell-panels", i18n.language)}
              className="px-8 py-4 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-colors"
            >
              {t("projects.bottomCta.sellButton")}
            </Link>
            <Link
              to={buildLocalizedPath("/contact", i18n.language)}
              className="px-8 py-4 bg-transparent text-white font-bold rounded-lg border-2 border-white/30 hover:border-gold hover:text-gold transition-colors"
            >
              {t("projects.bottomCta.quoteButton")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
